// src/pages/maintenance/VendorPicker.js
// Vendor/contractor picker for WorkOrderModal.js / PmTemplateModal.js — the option list merges
// existing MaintenanceVendors with SQF Hub contractors not yet imported ("populate from the
// contractor list available in the SQF hub"). Picking an SQF-sourced option transparently
// imports it into a real MaintenanceVendor first, so the caller always ends up with a vendorId.
// A "+ New Vendor" button opens QuickAddContractorDrawer for contractors that don't exist yet
// anywhere. See docs/MAINTENANCE_MODULE.md.
import React, { useEffect, useState, useCallback } from 'react';
import maintenanceService from '../../services/maintenanceService';
import SearchableSelect from '../../components/SearchableSelect';
import QuickAddContractorDrawer from './QuickAddContractorDrawer';

export default function VendorPicker({ value, onChange, placeholder = 'Internal / none' }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    maintenanceService.getVendors({ includeSqfContractors: true })
      .then(setOptions)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // `val` is the raw, still-prefixed dropdown value ("sqf:3" or a plain vendor id) — the "sqf:"
  // prefix is what disambiguates a selection, since MaintenanceVendor ids and SqfContractor ids
  // are separate sequences that can collide numerically.
  const handleSelect = async (val) => {
    if (!val) { onChange(''); return; }
    if (!String(val).startsWith('sqf:')) { onChange(val); return; }

    const sqfContractorId = String(val).slice(4);
    setImporting(true);
    try {
      const vendor = await maintenanceService.importSqfContractor(sqfContractorId);
      await load(); // refresh so the merged list no longer shows the now-imported contractor twice
      onChange(vendor.id);
    } finally {
      setImporting(false);
    }
  };

  const selectOptions = options.map(o => ({
    // SQF-sourced options are keyed by their SqfContractor id, which can collide with a real
    // MaintenanceVendor id — prefix to keep them unambiguous in the dropdown's own value space.
    value: o.source === 'sqf_contractor' ? `sqf:${o.id}` : o.id,
    label: `${o.name}${o.source === 'sqf_contractor' ? ' 🏗 From SQF Hub' : ''}`,
  }));

  // `value` (from the parent form) is always a real MaintenanceVendor id once something is
  // selected — handleSelect() resolves any SQF-sourced pick into a real vendor id before calling
  // onChange, so there's never a raw "sqf:" value to reconstruct here.
  return (
    <>
      <div className="d-flex gap-2">
        <div style={{ flex: 1 }}>
          <SearchableSelect
            options={selectOptions}
            value={value || ''}
            onChange={handleSelect}
            placeholder={loading || importing ? 'Loading…' : placeholder}
            disabled={loading || importing}
          />
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowDrawer(true)}>+ New Vendor</button>
      </div>

      <QuickAddContractorDrawer
        open={showDrawer}
        vendor={null}
        onClose={() => setShowDrawer(false)}
        onSaved={(vendor) => { setShowDrawer(false); load(); onChange(vendor.id); }}
      />
    </>
  );
}
